import React, { useRef, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { useDarkMode, useDataTableContext } from "../../../../../../hooks";
import Input from "../../../../Input";
import { getFontAwesomeIcon } from "../../../../../../utils";
import BasePill from "../../../../BasePill";
import { ColumnInstance } from "react-table";
import { useSearchParams, useLocation } from "react-router-dom";

type InputAndCheckProps<T extends object> = {
	closeOutSearch: (value: number | null) => void;
	setResetSearch: React.Dispatch<React.SetStateAction<boolean>>;
	pillColor?: string;
	column: ColumnInstance<T>;
	setEditingHeader: (value: number | null) => void;
	slug: string;
	columnSlug: string;
};

function updateFilterParam(
	searchParams: URLSearchParams,
	setSearchParams: (p: URLSearchParams, opts?: any) => void,
	key: string,
	values: string[]
) {
	// clone existing
	const next = new URLSearchParams(searchParams);
	// remove everything under this key
	next.delete(key);
	// re-append each value
	values.forEach((v) => next.append(key, v));
	// write it back
	setSearchParams(next, { replace: true });
}

const InputAndCheck = <T extends object>({
	closeOutSearch,
	setResetSearch,
	setEditingHeader,
	pillColor = "bg-supply-blue-600",
	column,
	slug,
	columnSlug,
}: InputAndCheckProps<T>) => {
	const { search: rawSearch } = useLocation();
	const { darkMode } = useDarkMode();
	const { searchCriteria, setSearchCriteria } = useDataTableContext();

	const containerRef = useRef<HTMLDivElement>(null);
	const [localSearchText, setLocalSearchText] = useState<string>("");
	const [searchParams, setSearchParams] = useSearchParams();
	const paramKey = `${slug}_${columnSlug}`;
	const criterionId = columnSlug;

	// const urlParts = searchParams.getAll(paramKey);

	// const existingCriterion = searchCriteria.find(
	// 	(c) => c.searchColumn.id === column.id
	// );
	// const contextValue =
	// 	existingCriterion && existingCriterion.submittedSearchText;

	// const pillValues = Array.isArray(contextValue)
	// 	? contextValue
	// 	: contextValue
	// 	? [contextValue]
	// 	: [];
	// 1️⃣ read raw URL
	const urlValue = searchParams.get(paramKey) ?? "";
	const urlParts = urlValue.split(":").filter(Boolean);

	// 2️⃣ see if React context has taken over yet
	const existingCriterion = searchCriteria.find(
		(c) => c.searchColumn.id === column.id
	);
	const contextValue =
		existingCriterion && existingCriterion.submittedSearchText;

	// 3️⃣ if we do have a contextValue, use it; otherwise fall back to URL parts
	const pillValues = existingCriterion
		? Array.isArray(contextValue)
			? contextValue
			: [contextValue]
		: urlParts;

	function updateFilterParam(key: string, values: string[]) {
		// ← clone from the LIVE searchParams, not rawSearch
		const current = new URLSearchParams(searchParams);
		current.delete(key);
		values.forEach((v) => current.append(key, v));
		setSearchParams(current, { replace: true });
	}

	const removeSingle = (value: string) => {
		// — React state
		setSearchCriteria((prev) =>
			prev
				.map((c) =>
					c.searchColumn.id === column.id
						? {
								...c,
								submittedSearchText: (Array.isArray(
									c.submittedSearchText
								)
									? c.submittedSearchText
									: [c.submittedSearchText]
								).filter((v) => v !== value),
						  }
						: c
				)
				.filter((c) =>
					Array.isArray(c.submittedSearchText)
						? c.submittedSearchText.length > 0
						: !!c.submittedSearchText
				)
		);

		// — URL
		const parts = (searchParams.get(paramKey) ?? "")
			.split(":")
			.filter((v) => v && v !== value);
		if (parts.length) {
			searchParams.set(paramKey, parts.join(":"));
		} else {
			searchParams.delete(paramKey);
		}
		setSearchParams(searchParams, { replace: true });
		closeOutSearch(null);
	};

	// Handle input change
	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setLocalSearchText(event.target.value);
	};

	// Handle submit
	const handleSubmitClick = () => {
		const trimmed = localSearchText.trim();

		// 1️⃣ Remove if empty
		if (!trimmed) {
			setSearchCriteria((prev) =>
				prev.filter((c) => c.searchColumn.id !== column.id)
			);
			searchParams.delete(paramKey);
			setSearchParams(searchParams, { replace: true });
		} else {
			// 2️⃣ Append into React state
			setSearchCriteria((prev) => {
				const idx = prev.findIndex(
					(c) => c.searchColumn.id === column.id
				);
				if (idx === -1) {
					return [
						...prev,
						{ searchColumn: column, submittedSearchText: trimmed },
					];
				}
				// build an array of old+new
				const old = prev[idx].submittedSearchText;
				const list = Array.isArray(old) ? [...old] : [old];
				if (!list.includes(trimmed)) list.push(trimmed);
				// replace with array
				return prev.map((c, i) =>
					i === idx ? { ...c, submittedSearchText: list } : c
				);
			});

			// 3️⃣ Append into the URL param with colons
			const existing = searchParams.get(paramKey)?.split(":") || [];
			if (!existing.includes(trimmed)) existing.push(trimmed);
			searchParams.set(paramKey, existing.join(":"));
			setSearchParams(searchParams, { replace: true });
		}
		setLocalSearchText("");

		setLocalSearchText("");
		setEditingHeader(null);
	};

	// Handle clear
	const handleIconClick = () => {
		// Remove criterion
		setSearchCriteria((prevCriteria) =>
			prevCriteria.filter(
				(criterion) => criterion.searchColumn.id !== column.id
			)
		);
		setLocalSearchText("");
		setResetSearch((prevResetSearch) => !prevResetSearch);
		closeOutSearch(null);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSubmitClick();
		}
	};

	return (
		<div ref={containerRef} className="">
			<div className="flex flex-col border-1 rounded-md border-navy-200 shadow-md">
				<div className="flex">
					<Input
						hasAutoFocus
						value={localSearchText}
						onKeyDown={handleKeyDown}
						required={false}
						id=""
						name=""
						type="text"
						onChange={handleInputChange}
						additionalClasses={`min-w-[250px] max-w-[250px] border-b-1 border-t-0 border-r-0  min-h-full rounded-tl-none rounded-tr-lg ${
							darkMode ? "" : " bg-navy-50 text-gray flex"
						}`}
						placeholder="Search"
						hasIcons
						firstIcon={
							localSearchText === "" ? (
								<AnimatePresence>
									<motion.div
										initial="initial"
										animate="animate"
										exit="exit"
										// variants={simpleFadeVariants}
										className="text-navy-400"
									>
										{getFontAwesomeIcon(
											"search",
											"regular"
										)}
									</motion.div>
								</AnimatePresence>
							) : undefined
						}
						iconPosition="both"
						secondIcon={
							<div
								className={`${
									darkMode
										? "bg-transparent hover:bg-teal-400 text-primary border-1 border-primary"
										: "border-transparent text-white min-w-9"
								}`}
							>
								<AnimatePresence>
									{localSearchText !== "" && (
										<motion.div
											className="flex justify-between min-w-4 text-navy-400 hover:cursor-pointer hover:text-primary"
											initial="initial"
											animate="animate"
											exit="exit"
										>
											<div
												className="bg-navy-50 pr-2 pl-1"
												onClick={handleIconClick}
											>
												{getFontAwesomeIcon(
													"Xmark",
													"regular"
												)}
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</div>
						}
						onIconClick={handleIconClick}
					/>
				</div>
				{/* Optionally display the active search criterion as a pill */}
				{pillValues.length > 0 && (
					<div className="bg-white py-2 px-2 rounded-md mt-2 flex flex-wrap gap-2">
						{pillValues.map((val) => (
							<BasePill
								key={val}
								text={val}
								type="span"
								additionalClasses={`${pillColor} text-white text-xs px-3 py-1 rounded-full w-fit`}
								cursorPointer
								secondIcon={
									<div className="text-white text-xxs">
										{getFontAwesomeIcon("x", "solid")}
									</div>
								}
								onClick={() => removeSingle(val || "")}
								hoverBackgroundColor={undefined}
							/>
						))}
					</div>
				)}
			</div>
			<AnimatePresence>
				{localSearchText !== "" && (
					<motion.div
						initial="initial"
						animate="animate"
						exit="exit"
						// variants={simpleFadeVariants}
						onClick={handleSubmitClick}
						className="text-md absolute right-3 hover:cursor-pointer top-2"
					>
						<div className="text-navy-400 hover:text-primary">
							{getFontAwesomeIcon("search", "solid")}
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default InputAndCheck;
